import { typographyTokens } from '../../../../theme/typographyTokens';
// TaskDependencyMappingTable.tsx
// This component renders a responsive, editable table for SEP task dependencies, allowing admin users to view and update task relationships, status, and phase. It uses MUI components and manages state for modal editing.

// Imports: MUI components, icons, hooks, types, and shared ActionButtons
import React, { useEffect, useState, useMemo } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Typography, Autocomplete, TextField, Select, MenuItem, FormControl, InputLabel, Box, Chip , useTheme, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import ModalWidget from '../../../../components/ModalWidget/ModalWidget';
import { transformQuillListToNested, nestedListStyles } from '../../../../utils/quillHtmlTransform';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useGetSepTasksQuery } from '../../../../services/sepSlice';
import { useUpdateTaskByAdminMutation } from '../../../../../frontend/services/tasksSlice/tasksSlice';
import { UpdateTaskByAdminBody } from '../../../../../shared/types/Task';
import { ActionButtons } from '../../../../../frontend/components/CancelSave';
import { getTaskStatusLabel } from '../../../../../shared/utils/taskStatus';

// Types for dependency objects and table rows
// PrecedingTask and FollowingTask are used for dependency mapping
// TaskRow represents a single row in the table

type PrecedingTask = { id: number; taskName: string };
type FollowingTask = { id: number; taskName: string };
type TaskRow = {
    taskName: string;
    shortName: string;
    description: string;
    departmentName: string;
    assigneeName: string;
    assigneeEmail: string;
    defaultReviewerName: string;
    defaultReviewerEmail: string;
    enabled: boolean;
    createdAt: string;
    startedAt: string;
    completedAt: string;
    precedingDependencies: PrecedingTask[];
    followingTasks: FollowingTask[];
    status: string;
    phase: string;
    id: number;
    taskTemplateID: number | null;
    isCustomTask: boolean;
};

// Static options for status and phase ordering
const statusOptions = ['todo', 'pending', 'complete', 'cancelled'];
const phaseOrderArr = ['validation', 'initiate', 'design', 'implement'];

// Responsive table styles for mobile and desktop
const tableStyles = {
    minWidth: 300,
    width: '100%',
    '@media (max-width: 600px)': {
        display: 'block',
        '& thead, & tbody, & tr, & th, & td': {
            display: 'block',
            width: '100%',
            boxSizing: 'border-box',
        },
        '& th, & td': {
            wordBreak: 'break-word',
            whiteSpace: 'pre-line',
        },
    },
};

// Main component: renders the SEP task dependency table and edit modal
const TaskDependencyMappingTable = ({ sepID, taskIdFilter }: { sepID: number; taskIdFilter?: number | null }) => {
    // State management for table data, modal, and editing fields
    const { data: sepTasks, error, isLoading, refetch } = useGetSepTasksQuery(sepID, { skip: !sepID });
    const [taskData, setTaskData] = useState<TaskRow[]>([]);
    const [allTasksData, setAllTasksData] = useState<TaskRow[]>([]); // All tasks for status lookup
    const [open, setOpen] = useState(false);
    const [editIdx, setEditIdx] = useState<number | null>(null);
    const [preceding, setPreceding] = useState<PrecedingTask[]>([]);
    const [following, setFollowing] = useState<FollowingTask[]>([]);
    const [editStatus, setEditStatus] = useState('');
    const [editPhase, setEditPhase] = useState('');
    const [editTaskName, setEditTaskName] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [viewOpen, setViewOpen] = useState(false);
    const [viewIdx, setViewIdx] = useState<number | null>(null);
    const [updateTaskByAdmin] = useUpdateTaskByAdminMutation();
    const theme = useTheme();

    // Effect: fetch and format SEP tasks, including dependency mapping
    useEffect(() => {
        if (!sepTasks || sepTasks.length === 0){
            setTaskData([]);
            setAllTasksData([]);
            return;
        }
        // Filter enabled tasks and format dependencies
        const enabledTasks = sepTasks.filter((task: any) => task.enabled).map((task: any) => ({
            ...task,
            parentTasks: task.parentTasks ? task.parentTasks.filter((pt: any) => pt.enabled) : [],
        }));
        
        if (enabledTasks.length === 0) {
            setTaskData([]);
            setAllTasksData([]);
            return;
        }
        
        // Map parent tasks to precedingDependencies (use ALL tasks for reference)
        const formattedTasks = enabledTasks.map((task: any) => {
            const preceding = task.parentTasks
                ? task.parentTasks.map((pt: any) => ({ id: pt.id, taskName: pt.name }))
                : [];
            return {
                taskName: task.name,
                shortName: task.shortName || '',
                description: task.description || '',
                departmentName: task.department?.name || '',
                assigneeName: task.assignee 
                    ? (task.assignee.displayName || 
                       (task.assignee.firstName && task.assignee.lastName 
                           ? `${task.assignee.lastName}, ${task.assignee.firstName}` 
                           : task.assignee.email || 'Unassigned'))
                    : 'Unassigned',
                assigneeEmail: task.assignee?.email || '',
                defaultReviewerName: task.defaultReviewer 
                    ? (task.defaultReviewer.displayName || 
                       (task.defaultReviewer.firstName && task.defaultReviewer.lastName 
                           ? `${task.defaultReviewer.lastName}, ${task.defaultReviewer.firstName}` 
                           : task.defaultReviewer.email || ''))
                    : '',
                defaultReviewerEmail: task.defaultReviewer?.email || '',
                enabled: task.enabled,
                createdAt: task.createdAt ? new Date(task.createdAt).toLocaleString() : '-',
                startedAt: task.startedAt ? new Date(task.startedAt).toLocaleString() : '-',
                completedAt: task.completedAt ? new Date(task.completedAt).toLocaleString() : '-',
                precedingDependencies: preceding,
                status: task.status || '',
                phase: task.phase || '',
                id: task.id,
                taskTemplateID: task.taskTemplateID || null,
                isCustomTask: task.taskTemplateID === null
            };
        });
        // Map following tasks based on dependency relationships (use ALL tasks)
        const formattedTasksWithFollowings: TaskRow[] = formattedTasks.map((task: any) => {
            const followings = formattedTasks
                .filter((t: any) => t.precedingDependencies.some((p: PrecedingTask) => p.taskName === task.taskName))
                .map((t: any) => ({ id: t.id, taskName: t.taskName }));
            return {
                ...task,
                followingTasks: followings,
            };
        });
        
        // Store all tasks for status lookup
        setAllTasksData(formattedTasksWithFollowings);
        
        // Filter by task ID AFTER calculating all dependencies
        if (taskIdFilter) {
            const filteredTasks = formattedTasksWithFollowings.filter((task: TaskRow) => task.id === taskIdFilter);
            setTaskData(filteredTasks);
        } else {
            setTaskData(formattedTasksWithFollowings);
        }
    }, [sepTasks, taskIdFilter]);

    // Options for Autocomplete fields
    const allTaskNames = taskData.map((row) => row.taskName);
    const allPrecedingOptions = taskData.map((row) => ({ id: row.id, taskName: row.taskName }));
    const allFollowingOptions = taskData.map((row) => ({ id: row.id, taskName: row.taskName }));

    // View details modal handlers
    const handleView = (rowIdx: number) => {
        setViewIdx(rowIdx);
        setViewOpen(true);
    };

    const handleViewClose = () => {
        setViewOpen(false);
        setViewIdx(null);
    };

    // Edit modal handlers: open, close, and save
    const handleEdit = (rowIdx: number) => {
        setEditIdx(rowIdx);
        setPreceding(taskData[rowIdx].precedingDependencies);
        setFollowing(taskData[rowIdx].followingTasks);
        setEditStatus(taskData[rowIdx].status);
        setEditPhase(taskData[rowIdx].phase);
        setEditTaskName(taskData[rowIdx].taskName);
        setEditDescription(taskData[rowIdx].description);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditIdx(null);
    };

    // Save handler: updates the task via mutation and refetches data
    const handleSave = async () => {
        if (editIdx !== null) {
            const updated = [...taskData];
            const oldTaskState = Object.assign({}, taskData[editIdx]);
            updated[editIdx] = {
                ...updated[editIdx],
                taskName: taskData[editIdx].isCustomTask ? editTaskName : taskData[editIdx].taskName,
                description: editDescription,
                precedingDependencies: preceding,
                followingTasks: following,
                status: editStatus,
                phase: editPhase,
            };
            const newTaskState = Object.assign({}, updated[editIdx]);


            // Prepare payload for backend update
            const changes: UpdateTaskByAdminBody = { oldTaskState: oldTaskState, newTaskState: newTaskState, sepID: sepID };

            await updateTaskByAdmin({ taskID: taskData[editIdx].id, taskDetails: changes, taskName: editTaskName })
                .unwrap()
                .then(() => {
                    refetch();
                    setOpen(false);
                    setEditIdx(null);
                    //   setTaskData(updated);
                })
                .catch((error: any) => {
                    console.error('Failed to update task:', error);
                });
        }

    };

    // Sort and group tasks by phase for table rendering
    const sortedData = [...taskData].sort((a, b) => {
        const phaseA = phaseOrderArr.indexOf(a.phase);
        const phaseB = phaseOrderArr.indexOf(b.phase);
        if (phaseA !== phaseB) return phaseA - phaseB;
        return 0;
    });
    const groupedByPhase: { [phase: string]: typeof taskData } = {};
    sortedData.forEach(row => {
        if (!groupedByPhase[row.phase]) groupedByPhase[row.phase] = [];
        groupedByPhase[row.phase].push(row);
    });

    // Loading, error, and empty state handling
    if (isLoading) return <Typography>Loading...</Typography>;
    if (error) return <Typography color="error">Failed to load SEP tasks.</Typography>;
    if (!taskData.length) return <Typography>{taskIdFilter ? `No task found with ID ${taskIdFilter} in this SEP.` : 'No tasks found for this SEP.'}</Typography>;
    // Main render: table and edit modal
    return (
        <>
            {/* Table header and SEP info */}
            <Paper sx={{ px: 2, py: 1, mb: 1, borderRadius: 2,  border: '1px solid #ddd' }}>
                <Typography sx={{ fontWeight: typographyTokens.fontWeights.bold, fontSize: '1.25rem' }}>
                    Task Details ( SEP {sepID} ){taskIdFilter ? ` - Task ID: ${taskIdFilter}` : ''}
                </Typography>
            </Paper>
            <TableContainer component={Paper}>
                <Table sx={tableStyles} aria-label="responsive table">
                    <TableHead>
                        <TableRow sx={{ borderBottom: '1px solid #222' }}>
                            <TableCell sx={{  fontWeight: typographyTokens.fontWeights.regular, fontSize: typographyTokens.fontSizes.body }}><b>Phase</b></TableCell>
                            <TableCell sx={{ fontWeight: typographyTokens.fontWeights.regular, fontSize: typographyTokens.fontSizes.body }}><b>Preceding Tasks</b></TableCell>
                            <TableCell sx={{  fontWeight: typographyTokens.fontWeights.regular, fontSize: typographyTokens.fontSizes.body }}><b>Task Name</b></TableCell>
                            <TableCell sx={{ fontWeight: typographyTokens.fontWeights.regular, fontSize: typographyTokens.fontSizes.body }}><b>Following Tasks</b></TableCell>
                            <TableCell sx={{ fontWeight: typographyTokens.fontWeights.regular, fontSize: typographyTokens.fontSizes.body }}><b>Actions</b></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {/* Render grouped rows by phase, with vertical phase cell and colored status */}
                        {phaseOrderArr.map(phase => {
                            const phaseRows = groupedByPhase[phase] || [];
                            if (phaseRows.length === 0) return null;
                            let phaseColor = '';
                            if (phase === 'validation') phaseColor = 'rgb(117, 141, 163)';
                            else if (phase === 'initiate') phaseColor = 'rgb(65, 105, 225)';
                            else if (phase === 'design') phaseColor = 'rgba(255, 165, 0, 0.6)';
                            else if (phase === 'implement') phaseColor = 'rgba(0, 128, 0, 0.6)';
                            return phaseRows.map((row, idx) => {
                                let statusColor = 'black';
                                if (row.status === 'todo') statusColor = 'rgb(35, 114, 185)';
                                else if (row.status === 'complete') statusColor = 'rgb(0, 128, 0)';
                                else if (row.status === 'pending') statusColor = 'grey';
                                return (
                                    <TableRow key={row.taskName}>
                                        {/* Render phase cell only for first row in group */}
                                        {idx === 0 && (
                                            <TableCell
                                                rowSpan={phaseRows.length}
                                                sx={{ background: phaseColor,  fontWeight: typographyTokens.fontWeights.bold, writingMode: 'vertical-rl', textAlign: 'center', fontSize: typographyTokens.fontSizes.body, minWidth: 32 }}
                                            >
                                                {phase.charAt(0).toUpperCase() + phase.slice(1)}
                                            </TableCell>
                                        )}
                                        {/* Preceding dependencies list with status color */}
                                        <TableCell sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                                            {row.precedingDependencies.length > 0 ? (
                                                <ol style={{ margin: 0, paddingLeft: 18 }}>
                                                    {row.precedingDependencies.map((dep: PrecedingTask, i: number) => {
                                                        const depTask = allTasksData.find(t => t.taskName === dep.taskName);
                                                        const depStatus = depTask ? depTask.status : '';
                                                        let depColor = 'black';
                                                        if (depStatus === 'todo') depColor = 'rgb(35, 114, 185)';
                                                        else if (depStatus === 'complete') depColor = 'rgb(0, 128, 0)';
                                                        else if (depStatus === 'pending') depColor = 'grey';
                                                        return (
                                                            <li key={dep.id}>
                                                                {dep.taskName}{' '}
                                                                <span style={{ color: depColor, fontWeight: typographyTokens.fontWeights.medium }}>({getTaskStatusLabel(depStatus)})</span>
                                                            </li>
                                                        );
                                                    })}
                                                </ol>
                                            ) : '-'}
                                        </TableCell>
                                        {/* Task name and status */}
                                        <TableCell>
                                            {row.taskName}{' '}
                                            <span style={{ color: statusColor, fontWeight: typographyTokens.fontWeights.medium }}>
                                                ({getTaskStatusLabel(row.status)})
                                            </span>
                                        </TableCell>
                                        {/* Following dependencies list with status color */}
                                        <TableCell sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                                            {row.followingTasks.length > 0 ? (
                                                <ol style={{ margin: 0, paddingLeft: 18 }}>
                                                    {row.followingTasks.map((dep: FollowingTask, i: number) => {
                                                        const depTask = allTasksData.find(t => t.taskName === dep.taskName);
                                                        const depStatus = depTask ? depTask.status : '';
                                                        let depColor = 'black';
                                                        if (depStatus === 'todo') depColor = 'rgb(35, 114, 185)';
                                                        else if (depStatus === 'complete') depColor = 'rgb(0, 128, 0)';
                                                        else if (depStatus === 'pending') depColor = 'grey';
                                                        return (
                                                            <li key={dep.id}>
                                                                {dep.taskName}{' '}
                                                                <span style={{ color: depColor, fontWeight: typographyTokens.fontWeights.medium }}>({getTaskStatusLabel(depStatus)})</span>
                                                            </li>
                                                        );
                                                    })}
                                                </ol>
                                            ) : '-'}
                                        </TableCell>
                                        {/* View and Edit buttons */}
                                        <TableCell>
                                            <IconButton aria-label="view" onClick={() => handleView(taskData.findIndex(t => t.taskName === row.taskName))} title="View Details">
                                                <VisibilityIcon />
                                            </IconButton>
                                            <IconButton aria-label="edit" onClick={() => handleEdit(taskData.findIndex(t => t.taskName === row.taskName))} title="Edit Task">
                                                <EditIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            });
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {/* Edit modal for updating task details - now using ModalWidget */}
            <ModalWidget isOpen={open} title="Edit Task Details" onClose={handleClose}>
                <Box sx={{ p: 3, minWidth: 500 }}>
                    {editIdx !== null && taskData[editIdx].isCustomTask ? (
                        <TextField
                            fullWidth
                            label="Task Name"
                            value={editTaskName}
                            onChange={(e) => setEditTaskName(e.target.value)}
                            sx={{ mt: 3 }}
                            helperText="Custom task - name can be edited"
                        />
                    ) : (
                        <Typography variant="subtitle2" sx={{ mt: 3, mb: 2, fontWeight: typographyTokens.fontWeights.semibold }}>
                            Task : <span style={{ fontWeight: typographyTokens.fontWeights.semibold }}>{editIdx !== null ? taskData[editIdx].taskName : ''}</span>
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                (Template task - name cannot be edited)
                            </Typography>
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel id="status-label">Status</InputLabel>
                            <Select
                                labelId="status-label"
                                value={editStatus}
                                label="Status"
                                onChange={e => setEditStatus(e.target.value)}
                            >
                                {statusOptions.map(option => (
                                    <MenuItem key={option} value={option}>{getTaskStatusLabel(option)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel id="phase-label">Phase</InputLabel>
                            <Select
                                labelId="phase-label"
                                value={editPhase}
                                label="Phase"
                                onChange={e => setEditPhase(e.target.value)}
                            >
                                {phaseOrderArr.map(option => (
                                    <MenuItem key={option} value={option}>{option.charAt(0).toUpperCase() + option.slice(1)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Autocomplete
                            multiple
                            options={allPrecedingOptions.filter((opt) => opt.taskName !== (editIdx !== null ? taskData[editIdx].taskName : ''))}
                            getOptionLabel={(option) => option.taskName}
                            value={preceding}
                            onChange={(_event: React.SyntheticEvent, value: PrecedingTask[]) => setPreceding(value)}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => <TextField {...params} label="Preceding Tasks" variant="outlined" margin="normal" />}
                        />
                        <Autocomplete
                            multiple
                            options={allFollowingOptions.filter((opt) => opt.taskName !== (editIdx !== null ? taskData[editIdx].taskName : ''))}
                            getOptionLabel={(option) => option.taskName}
                            value={following}
                            onChange={(_event: React.SyntheticEvent, value: FollowingTask[]) => setFollowing(value)}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => <TextField {...params} label="Following Tasks" variant="outlined" margin="normal" />}
                        />
                        <FormControl sx={{ mt: 2, width: '100%' }} size="small">
                            <TextField
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        Task Description
                                        <Tooltip
                                            title={
                                                <span style={{ fontSize: typographyTokens.fontSizes.lg, lineHeight: '1.4' }}>
                                                    <b>How to Style and Save a Text Description</b><br /><br />
                                                    1. Write your text description in Microsoft Word.<br />
                                                    2. Save the file as “Web Page, Filtered (*.htm, *.html)” or “Web Page (*.htm, *.html)”.<br />
                                                    3. Open the saved file using Notepad.<br />
                                                    4. Copy all the HTML content from Notepad.<br />
                                                    5. Paste the HTML content into the textarea box provided here and save your changes.
                                                </span>
                                            }
                                            placement="top"
                                            arrow
                                        >
                                            <IconButton size="small" tabIndex={-1}>
                                                <InfoOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                }
                                value={editDescription}
                                onChange={e => setEditDescription(e.target.value)}
                                multiline
                                rows={6}
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                sx={{
                                    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.white.main,
                                    color: theme.palette.mode === 'dark' ? theme.palette.text.primary : "#222",
                                    borderRadius: '6px',
                                }}
                                placeholder="Ex. We need to understand the scope to determine which teams will need to review this request. Please select all that apply."
                                InputLabelProps={{ shrink: true }}
                            />
                            <Typography
                                sx={{
                                    margin: '5px 0px',
                                    color: '#646669',
                                    fontSize: typographyTokens.fontSizes.xs,
                                }}
                            >
                                You can paste HTML from Word as described in the info icon.
                            </Typography>
                        </FormControl>
                        <Box sx={{ mt: 2 }}>
                            <ActionButtons
                                handleCancel={handleClose}
                                handleSave={handleSave}
                                saveLabel={'Update'}
                            />
                        </Box>
                    </Box>
                </Box>
            </ModalWidget>
            {/* View Details modal - shows all task information in table format */}
            <Dialog open={viewOpen} onClose={handleViewClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.xl, borderBottom: '1px solid #ddd', pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Task Details (ID: {viewIdx !== null ? taskData[viewIdx].id : ''})
                    <Button variant="contained" size="small" onClick={() => { handleViewClose(); if (viewIdx !== null) handleEdit(viewIdx); }}>Edit Task</Button>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {viewIdx !== null && (
                        <Table size="small">
                            <TableBody>
                                <TableRow sx={{ borderBottom: '1px solid #eee' }}>
                                    <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold, width: 160,  py: 1.5 }}>Task ID</TableCell>
                                    <TableCell sx={{ py: 1.5 }}>{taskData[viewIdx].id}</TableCell>
                                </TableRow>
                                <TableRow sx={{ borderBottom: '1px solid #eee' }}>
                                    <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold, py: 1.5 }}>Task Name</TableCell>
                                    <TableCell sx={{ py: 1.5 }}>{taskData[viewIdx].taskName}</TableCell>
                                </TableRow>
                                <TableRow sx={{ borderBottom: '1px solid #eee' }}>
                                    <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold,  py: 1.5 }}>Short Name</TableCell>
                                    <TableCell sx={{ py: 1.5 }}>{taskData[viewIdx].shortName || '-'}</TableCell>
                                </TableRow>
                                <TableRow sx={{ borderBottom: '1px solid #eee' }}>
                                    <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold,  py: 1.5 }}>SEP</TableCell>
                                    <TableCell sx={{ py: 1.5 }}>{sepID}</TableCell>
                                </TableRow>
                                <TableRow sx={{ borderBottom: '1px solid #eee' }}>
                                    <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold,  py: 1.5 }}>Task Status</TableCell>
                                    <TableCell sx={{ py: 1.5 }}>
                                        <Chip 
                                            label={getTaskStatusLabel(taskData[viewIdx].status)} 
                                            size="small" 
                                            sx={{ 
                                                bgcolor: taskData[viewIdx].status === 'complete' ? '#4caf50' : 
                                                         taskData[viewIdx].status === 'todo' ? '#2196f3' : 
                                                         taskData[viewIdx].status === 'pending' ? '#9e9e9e' : '#f44336',
                                                
                                                fontWeight: typographyTokens.fontWeights.medium
                                            }} 
                                        />
                                    </TableCell>
                                </TableRow>
                                <TableRow sx={{ borderBottom: '1px solid #eee' }}>
                                    <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold, py: 1.5 }}>Phase</TableCell>
                                    <TableCell sx={{ py: 1.5 }}>
                                        <Chip 
                                            label={taskData[viewIdx].phase} 
                                            size="small" 
                                            sx={{ 
                                                bgcolor: taskData[viewIdx].phase === 'validation' ? '#758da3' : 
                                                         taskData[viewIdx].phase === 'initiate' ? '#4169e1' : 
                                                         taskData[viewIdx].phase === 'design' ? '#ffa500' : '#008000',
                                                
                                                fontWeight: typographyTokens.fontWeights.medium
                                            }} 
                                        />
                                    </TableCell>
                                </TableRow>
                                <TableRow sx={{ borderBottom: '1px solid #eee' }}>
                                    <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold,  py: 1.5 }}>Department</TableCell>
                                    <TableCell sx={{ py: 1.5 }}>{taskData[viewIdx].departmentName || '-'}</TableCell>
                                </TableRow>
                                <TableRow sx={{ borderBottom: '1px solid #eee' }}>
                                    <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold, py: 1.5 }}>Assignee</TableCell>
                                    <TableCell sx={{ py: 1.5 }}>
                                        {taskData[viewIdx].assigneeName}
                                        {taskData[viewIdx].assigneeEmail && taskData[viewIdx].assigneeName !== taskData[viewIdx].assigneeEmail && (
                                            <Typography variant="caption" sx={{  ml: 1 }}>({taskData[viewIdx].assigneeEmail})</Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                                <TableRow sx={{ borderBottom: '1px solid #eee' }}>
                                    <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold,  py: 1.5 }}>Default Reviewer</TableCell>
                                    <TableCell sx={{ py: 1.5 }}>
                                        {taskData[viewIdx].defaultReviewerName || '-'}
                                        {taskData[viewIdx].defaultReviewerEmail && taskData[viewIdx].defaultReviewerName !== taskData[viewIdx].defaultReviewerEmail && (
                                            <Typography variant="caption" sx={{  ml: 1 }}>({taskData[viewIdx].defaultReviewerEmail})</Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                                <TableRow sx={{ borderBottom: '1px solid #eee' }}>
                                    <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold, py: 1.5 }}>Enabled</TableCell>
                                    <TableCell sx={{ py: 1.5 }}>{taskData[viewIdx].enabled ? 'Yes' : 'No'}</TableCell>
                                </TableRow>
                                <TableRow sx={{ borderBottom: '1px solid #eee' }}>
                                    <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold, py: 1.5 }}>Template Task</TableCell>
                                    <TableCell sx={{ py: 1.5 }}>
                                        {taskData[viewIdx].isCustomTask ? 'No (Custom Task)' : `Yes (Template ID: ${taskData[viewIdx].taskTemplateID})`}
                                    </TableCell>
                                </TableRow>
                                <TableRow sx={{ borderBottom: '1px solid #eee' }}>
                                    <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold, py: 1.5 }}>Created At</TableCell>
                                    <TableCell sx={{ py: 1.5 }}>{taskData[viewIdx].createdAt}</TableCell>
                                </TableRow>
                                <TableRow sx={{ borderBottom: '1px solid #eee' }}>
                                    <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold,  py: 1.5 }}>Started At</TableCell>
                                    <TableCell sx={{ py: 1.5 }}>{taskData[viewIdx].startedAt}</TableCell>
                                </TableRow>
                                <TableRow sx={{ borderBottom: '1px solid #eee' }}>
                                    <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold,  py: 1.5 }}>Completed At</TableCell>
                                    <TableCell sx={{ py: 1.5 }}>{taskData[viewIdx].completedAt}</TableCell>
                                </TableRow>
                                <TableRow sx={{ borderBottom: '1px solid #eee' }}>
                                    <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold, py: 1.5 }}>Description</TableCell>
                                    <TableCell sx={{ py: 1.5 }}>
                                        {taskData[viewIdx].description ? (
                                            <Box 
                                                dangerouslySetInnerHTML={{ __html: transformQuillListToNested(taskData[viewIdx].description) }} 
                                                sx={{ 
                                                    '& p': { margin: '0.5em 0' },
                                                    ...nestedListStyles,
                                                    maxHeight: '300px',
                                                    overflowY: 'auto'
                                                }}
                                            />
                                        ) : '-'}
                                    </TableCell>
                                </TableRow>
                                <TableRow sx={{ borderBottom: '1px solid #eee' }}>
                                    <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold,  py: 1.5 }}>Preceding Tasks</TableCell>
                                    <TableCell sx={{ py: 1.5 }}>
                                        {taskData[viewIdx].precedingDependencies.length > 0 ? (
                                            <ol style={{ margin: 0, paddingLeft: 18 }}>
                                                {taskData[viewIdx].precedingDependencies.map((dep) => (
                                                    <li key={dep.id}>{dep.taskName}</li>
                                                ))}
                                            </ol>
                                        ) : '-'}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold, py: 1.5 }}>Following Tasks</TableCell>
                                    <TableCell sx={{ py: 1.5 }}>
                                        {taskData[viewIdx].followingTasks.length > 0 ? (
                                            <ol style={{ margin: 0, paddingLeft: 18 }}>
                                                {taskData[viewIdx].followingTasks.map((dep) => (
                                                    <li key={dep.id}>{dep.taskName}</li>
                                                ))}
                                            </ol>
                                        ) : '-'}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
                <DialogActions sx={{ borderTop: '1px solid #eee', p: 1,}}>
                    <Button onClick={handleViewClose} variant="outlined">CLOSE</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default TaskDependencyMappingTable;